import * as Arr from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/function';
import * as Hooks from 'preact/hooks';
import * as TrackingActions from '../../store/actions/app';
import { useDispatch, useSelector } from '../../store/redux';
import * as Selections from '../../store/selections';
import * as Selectors from '../../store/selectors';
import { ConfigurationUndoState } from './configurationUndoState';

export const useUndoRedo = () => {
    const dispatch = useDispatch();

    const [undoStack, setUndoStack] = Hooks.useState<Array<ConfigurationUndoState>>([]);
    const [redoStack, setRedoStack] = Hooks.useState<Array<ConfigurationUndoState>>([]);

    const productSelectionsByPart = useSelector(Selectors.Selections.productSelectionsByPart);
    const buildPropertySelections = useSelector(Selectors.Selections.buildPropertySelections);
    const showUndoRedo = useSelector(Selectors.Initialisation.inConfiguration);

    const undo = () => {
        // get current state of selection and make undo state
        const selections: ConfigurationUndoState = {
            buildPropertySelections,
            productSelectionsByPart,
        };
        // push this onto the redo stack
        setRedoStack([...redoStack, selections]);
        // take last item of undo stack and set the selections slice with it
        pipe(
            O.Do,
            O.bind('prevSelections', () => Arr.last(undoStack)),
            O.bind('newUndoStack', () => Arr.init(undoStack)),
            O.map(({ prevSelections, newUndoStack }) => {
                setUndoStack(newUndoStack);

                if (
                    prevSelections.productSelectionsByPart &&
                    prevSelections.buildPropertySelections
                ) {
                    dispatch(
                        Selections.setProductSelectionsByPart({
                            productSelectionsByPart: prevSelections.productSelectionsByPart,
                        }),
                    );
                    dispatch(
                        Selections.updateBuildPropertySelections({
                            newSelections: prevSelections.buildPropertySelections,
                        }),
                    );
                    dispatch(TrackingActions.undoAction());
                }
            }),
        );
    };

    const redo = () => {
        // get current state of selection and make undo state
        const selections: ConfigurationUndoState = {
            buildPropertySelections,
            productSelectionsByPart,
        };
        // push this onto the undo stack
        setUndoStack([...undoStack, selections]);

        // take last item of redo stack and set the selections slice with it
        pipe(
            O.Do,
            O.bind('prevSelections', () => Arr.last(redoStack)),
            O.bind('newRedoStack', () => Arr.init(redoStack)),
            O.map(({ prevSelections, newRedoStack }) => {
                setRedoStack(newRedoStack);

                if (
                    prevSelections.productSelectionsByPart &&
                    prevSelections.buildPropertySelections
                ) {
                    dispatch(
                        Selections.setProductSelectionsByPart({
                            productSelectionsByPart: prevSelections.productSelectionsByPart,
                        }),
                    );
                    dispatch(
                        Selections.updateBuildPropertySelections({
                            newSelections: prevSelections.buildPropertySelections,
                        }),
                    );
                    dispatch(TrackingActions.redoAction());
                }
            }),
        );
    };

    /** This update function should be called *before* making a change, so the current configuration can be captured for the undo stack */
    const update = () => {
        setRedoStack([]);

        const selections: ConfigurationUndoState = {
            buildPropertySelections,
            productSelectionsByPart,
        };

        setUndoStack([...undoStack, selections]);
    };

    Hooks.useEffect(() => {
        setUndoStack([]);
        setRedoStack([]);
    }, [showUndoRedo]);

    return {
        undo,
        redo,
        undoCount: undoStack.length,
        redoCount: redoStack.length,
        /** This update function should be called *before* making a change, so the current configuration can be captured for the undo stack */
        update,
        showUndoRedo,
    };
};
